import React, {
    createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import moment from 'moment';
import { URLS } from '../helpers/URLS';

const AssistantContext = createContext(null);

export const useAssistant = () => {
    const context = useContext(AssistantContext);
    if (!context) throw new Error('useAssistant must be used inside an AssistantProvider');
    return context;
};

const BASE = '/api/v1/';
const HISTORY_TURNS = 20;
const LOCATION_DENIED = 'Location permission is needed for Work From Home';
/* a turn may take up to the server's 45 s deadline */
const TIMEOUT_MS = 60000;
const FINAL = ['executed', 'failed', 'unknown', 'cancelled', 'expired'];
const EXPIRED = 'This action expired. Please ask again.';
const ALREADY = /^This action was already (\w+)$/;

/* Bare axios rather than useAxios, for the same reason chat does it: that hook
   drives the global page spinner, and a conversation must not flash it. */
const request = async (method, url, body) => {
    try {
        const res = await axios[method](BASE + url, body, { timeout: TIMEOUT_MS });
        return res.data;
    } catch (error) {
        const payload = error && error.response && error.response.data;
        if (payload && typeof payload === 'object') return payload;
        return { status: 0, success: false, message: 'Cannot reach the server' };
    }
};

/* The browser's position, or null when it is refused or unavailable. */
const currentPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation || !navigator.geolocation.getCurrentPosition) {
        resolve(null);
        return;
    }
    navigator.geolocation.getCurrentPosition(
        (position) => resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
        }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000 },
    );
});

/* The status a confirm/cancel refusal says the action already has, or null
   when the refusal is not about its status. */
const statusFromRefusal = (message) => {
    if (message === EXPIRED) return 'expired';
    const match = ALREADY.exec(String(message || ''));
    return match ? match[1] : null;
};

/* What the model is told about a card that reached a final state. */
const outcomeNote = (action) => {
    const ref = action.result && action.result.reference;
    return `[Action ${action.tool} ${action.status}${ref ? `, ref ${ref}` : ''}]`;
};

let seq = 0;
const nextId = () => { seq += 1; return `m${seq}`; };

/* In memory only, by design: the conversation is gone on reload. */
export const AssistantProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [messages, setMessages] = useState([]);
    const inputRef = useRef(null);
    /* always the latest list, for building history inside an async handler */
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const update = useCallback((fn) => {
        setMessages((current) => {
            const next = fn(current);
            messagesRef.current = next;
            return next;
        });
    }, []);

    /* cards already noted in history, so a card is noted once */
    const notedRef = useRef(new Set());

    /* A hidden assistant turn: sent as history, never rendered. */
    const noteOutcome = useCallback((action) => {
        if (!FINAL.includes(action.status) || notedRef.current.has(action.id)) return;
        notedRef.current.add(action.id);
        update((current) => [...current, {
            id: nextId(), role: 'assistant', content: outcomeNote(action), hidden: true,
        }]);
    }, [update]);

    /* Replace one card wherever it sits, by id. */
    const patchAction = useCallback((id, patch) => update((current) => current.map((message) => (
        message.actions && message.actions.some((a) => a.id === id)
            ? { ...message, actions: message.actions.map((a) => (a.id === id ? { ...a, ...patch } : a)) }
            : message
    ))), [update]);

    /* Apply a confirm/cancel answer to its card. Returns the card's new view. */
    const settle = useCallback((action, res) => {
        if (res.success && res.data && res.data.action) {
            const view = { ...action, ...res.data.action };
            patchAction(action.id, { ...res.data.action, busy: false, notice: undefined });
            noteOutcome(view);
            return view;
        }
        const message = res.message || 'Something went wrong. Please try again.';
        const status = statusFromRefusal(res.message);
        if (!status) {
            patchAction(action.id, { busy: false, notice: message });
            return action;
        }
        /* the server already moved on: adopt its status, no live buttons */
        const view = { ...action, status };
        patchAction(action.id, { status, busy: false, notice: message });
        noteOutcome(view);
        return view;
    }, [patchAction, noteOutcome]);

    const send = useCallback(async (text) => {
        const content = String(text || '').trim();
        if (!content) return;

        /* the last 20 text turns before this one; failed turns are not history */
        const history = messagesRef.current
            .filter((m) => !m.failed && m.content)
            .slice(-HISTORY_TURNS)
            .map((m) => ({ role: m.role, content: m.content.slice(0, 2000) }));

        update((current) => [...current, { id: nextId(), role: 'user', content }]);
        setSending(true);

        const res = await request('post', URLS.assistantMessage, {
            message: content,
            history,
            today: moment().format('YYYY-MM-DD'),
            tzOffset: new Date().getTimezoneOffset(),
        });
        setSending(false);

        /* a card sent for revision is live again unless a new card replaces it below */
        const settleRevising = (a) => (a.revising ? { ...a, revising: false } : a);

        if (!res.success) {
            update((current) => [...current.map((message) => (message.actions
                ? { ...message, actions: message.actions.map(settleRevising) }
                : message)), {
                id: nextId(), role: 'assistant', content: res.message || 'Something went wrong. Please try again.', failed: true,
            }]);
            return;
        }

        const { reply, actions = [], links = [] } = res.data;
        update((current) => {
            /* a new card for a tool supersedes the older one of the same tool */
            const tools = new Set(actions.map((a) => a.tool));
            const next = current.map((message) => (message.actions
                ? {
                    ...message,
                    actions: message.actions.map((a) => settleRevising(
                        a.status === 'pending' && tools.has(a.tool) && !actions.some((n) => n.id === a.id)
                            ? { ...a, status: 'cancelled', ...(a.revising ? { edited: true } : {}) } : a)),
                }
                : message));
            return [...next, { id: nextId(), role: 'assistant', content: reply, actions, links }];
        });
    }, [update]);

    const confirm = useCallback(async (action) => {
        patchAction(action.id, { busy: true, notice: undefined });

        let body = {};
        if (action.requiresLocation) {
            const location = await currentPosition();
            if (!location) {
                patchAction(action.id, { busy: false, notice: LOCATION_DENIED });
                return;
            }
            body = { location };
        }

        const res = await request('post', URLS.assistantConfirm(action.id), body);
        settle(action, res);
    }, [patchAction, settle]);

    const cancel = useCallback(async (action) => {
        patchAction(action.id, { busy: true, notice: undefined });
        const res = await request('post', URLS.assistantCancel(action.id), {});
        return settle(action, res);
    }, [patchAction, settle]);

    /* Inline Edit: the changed fields go to the agent as an ordinary message, so
       they are re-validated by prepare and come back as a new card to confirm. */
    const revise = useCallback((action, changes) => {
        patchAction(action.id, { revising: true, notice: undefined });
        const list = changes.map((field) => `${field.label}: ${field.value}`).join('; ');
        return send(`Update the "${action.title}" card: ${list}. Keep everything else the same.`);
    }, [patchAction, send]);

    const value = useMemo(() => ({
        open, setOpen, sending, messages, inputRef, send, confirm, cancel, revise,
    }), [open, sending, messages, send, confirm, cancel, revise]);

    return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

export default AssistantContext;
